import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FlexRow } from 'ui-components/components/flex/row';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import { applyFilterSet } from '../../../../store/orders/filtering/orders.filtering.actions';
import { SHIPMENT_FILTERS_STATE_KEY } from '../../../shipping-page/constants/shipment-filter-constants';
import { OPERATIONS_CARD_DISPLAYED, OPERATIONS_CARD_CLICKED } from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';

import type { CardType } from './types';
import { CARD_REGISTRY } from './constants/card-registry';
import { useCardData } from './hooks/use-card-data';
import style from './operations-cards.module.less';

const LOADING_PLACEHOLDER = '—';

type OperationsCardProps = {
  cardType: CardType;
};

export const OperationsCard = ({ cardType }: OperationsCardProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();

  const cardData = useCardData(cardType);
  const config = CARD_REGISTRY[cardType];
  const { icon, labelMessage, viewLinkMessage, route, filterSet, shipmentFilters } = config;

  const label = intl.formatMessage(labelMessage);
  const viewLinkLabel = intl.formatMessage(viewLinkMessage);
  const hasTrackedDisplayRef = useRef(false);
  useEffect(() => {
    if (hasTrackedDisplayRef.current) return;
    hasTrackedDisplayRef.current = true;
    Segment.trackEvent(OPERATIONS_CARD_DISPLAYED, { card_type: cardType });
  }, [cardType]);

  const handleClick = useCallback(() => {
    Segment.trackEvent(OPERATIONS_CARD_CLICKED, { card_type: cardType });

    if (filterSet) {
      dispatch(applyFilterSet(filterSet, { fromCard: true }));
    }

    const routeState = shipmentFilters ? { [SHIPMENT_FILTERS_STATE_KEY]: shipmentFilters } : undefined;

    navigate(route, { state: routeState });
  }, [dispatch, filterSet, shipmentFilters, navigate, route, cardType]);

  return (
    <Button ariaLabel={label} className={style['operations-card']} onClick={handleClick} theme={THEME_CONSTANTS.unstyled}>
      <FlexRow className={style['operations-card-content']}>
        <FlexColumn className={style['operations-card-top']} gap={Gap.Space4}>
          <Text className={style['operations-label']} textType={TextType.Label} value={label} />
          <div className={style['operations-value']}>{cardData.value ?? LOADING_PLACEHOLDER}</div>
          <FlexRow className={style['operations-view-link']} gap={Gap.Space4}>
            <Text textType={TextType.Label} value={viewLinkLabel} />
            <FontAwesomeIcon className={style['operations-link-arrow']} icon={faArrowRight} />
          </FlexRow>
        </FlexColumn>
        <img alt='' className={style['operations-icon']} src={icon} />
      </FlexRow>
    </Button>
  );
};
